import { DefaultPassThrough } from '@primevue/core'
import type {
  CheckboxPassThroughAttributes,
  CheckboxPassThroughMethodOptions,
  CheckboxPassThroughOptions,
  CheckboxProps as PrimeVueCheckboxProps
} from 'primevue/checkbox'
import type { CheckboxDesignTokens } from '@primeuix/themes/types/checkbox'
import Checkbox from './Checkbox.vue'
import { CheckboxGroupPassThroughOptions } from 'primevue/checkboxgroup'

export interface CheckboxProps extends PrimeVueCheckboxProps {
  /**
   * Uses custom color styles for the checkbox.
   */
  color?: 'monochrome' | 'accent'
}

export const CheckboxPassthroughConfig: DefaultPassThrough<CheckboxPassThroughOptions> = {
  root(options: CheckboxPassThroughMethodOptions): CheckboxPassThroughAttributes {
    return {
      class: '!tw-text-sm !tw-leading-tight'
    }
  },
  box(options: CheckboxPassThroughMethodOptions): CheckboxPassThroughAttributes {
    const isChecked = options.context.checked
    const isDisabled = options.context.disabled
    function hasColor(obj: unknown): obj is { color?: string } {
      return typeof obj === 'object' && obj !== null && 'color' in obj
    }
    const color =
      (hasColor(options.props) && options.props.color) ||
      (hasColor(options.attrs) && options.attrs.color) ||
      'monochrome'
    const style = [`tw-checkbox`]
    if (isChecked && !isDisabled) {
      style.push(`${color} tw-border-border-default hover:tw-border-hover focus:tw-border-focus`)
    }
    if (isDisabled) {
      style.push('tw-cursor-not-allowed')
      style.push(
        '!tw-bg-icon-disabled !tw-border-icon-disabled hover:tw-border-icon-disabled focus:tw-border-icon-disabled'
      )
    }
    return {
      class: style.join(' ')
    }
  },
  icon(options: CheckboxPassThroughMethodOptions): CheckboxPassThroughAttributes {
    return {
      class: `tw-stroke-current tw-stroke-[2rem]`
    }
  }
}

export const CheckboxGroupPassthroughConfig: DefaultPassThrough<CheckboxGroupPassThroughOptions> = {
  root: {
    class: 'tw-gap-2'
  }
}

export const CheckboxStyleConfig: CheckboxDesignTokens = {
  root: {
    checkedDisabledBorderColor: 'var(--icon-disabled)',
    disabledBackground: 'var(--icon-disabled)',
    shadow: 'none',
    sm: {
      width: '16px',
      height: '16px'
    },
    lg: {
      width: '20px',
      height: '20px'
    },
    borderColor: 'var(--border-default-color)',
    borderRadius: '3px'
  },
  icon: {
    sm: {
      size: '0.75rem'
    },
    lg: {
      size: '0.75rem'
    },
    disabledColor: 'var(--color-surface-default)'
  }
}

export enum CheckboxColors {
  Monochrome = 'monochrome',
  Accent = 'accent'
}

export enum CheckboxSizes {
  Small = 'small',
  Large = 'large'
}

export default Checkbox
